import {useDispatch} from 'react-redux';

import {useMutation} from '@apollo/react-hooks';

import {ActionTypes} from 'nutshell-core/session/session-types';
import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import type {
    AgentStatusUpdateInput,
    UpdateAgentStatus as UpdateAgentStatusMutation,
    UpdateAgentStatusVariables as UpdateAgentStatusMutationVariables,
} from 'nutshell-graphql-types';

import GET_INBOX_DASHBOARD_CHECKLIST from '../../../dashboard/graphql/queries/get-inbox-dashboard-checklist.graphql';

import {nutApolloClient} from '../../../../../apollo-client';
// @ts-expect-error
import {refreshUser} from '../../../../../user';
import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {useUpdateUserSetting} from '../../../../core/use-update-user-setting';

import CHAT_AGENT_FRAGMENT from '../../graphql/fragments/chat-agent-fragment.graphql';

import UPDATE_AGENT_STATUS from '../mutations/update-agent-status.graphql';

export function useUpdateAgentStatus() {
    const dispatch = useDispatch();

    const [updateAgentStatus, loading] = useMutation<
        UpdateAgentStatusMutation,
        UpdateAgentStatusMutationVariables
    >(UPDATE_AGENT_STATUS, {
        refetchQueries: [{query: GET_INBOX_DASHBOARD_CHECKLIST}],
    });
    const updateSetting = useUpdateUserSetting();

    const {user} = useGetSessionUser();
    const currentUserId = user && user.id;

    return {
        updateAgentStatus: (input: AgentStatusUpdateInput) => {
            const currentAgent = nutApolloClient.readFragment({
                id: input.userId,
                fragment: CHAT_AGENT_FRAGMENT,
                fragmentName: 'ChatAgent',
            });

            if (!currentAgent) return;

            return updateAgentStatus({
                variables: {
                    input,
                },
                update: (proxy, mutationResults) => {
                    const newStatus =
                        mutationResults.data &&
                        mutationResults.data.chatAgentStatusUpdate &&
                        mutationResults.data.chatAgentStatusUpdate.agentStatus;

                    if (!newStatus) return;

                    proxy.writeFragment({
                        id: input.userId,
                        fragment: CHAT_AGENT_FRAGMENT,
                        fragmentName: 'ChatAgent',
                        data: {
                            ...currentAgent,
                            agentStatus: newStatus,
                            agentPresence: newStatus === 'ONLINE' ? 'ONLINE' : 'OFFLINE',
                        },
                    });

                    // Dispatch Redux action to update session user state
                    if (input.userId === currentUserId) {
                        updateSetting({
                            userId: input.userId,
                            settingName: Nut_Settings_User.SETTING_HAS_CHANGED_AGENT_STATUS,
                            value: '1',
                            propertyOnUser: Nut_Settings_User.SETTING_HAS_CHANGED_AGENT_STATUS,
                        });
                        dispatch({
                            type: ActionTypes.SESSION_UPDATE_AGENT_STATUS,
                            payload: {
                                agentStatus: newStatus,
                            },
                        });
                        refreshUser(input.userId);
                    }
                },
            });
        },
        isUpdating: loading,
    };
}
